import cn from 'classnames';
import { useIntl } from 'react-intl';

import { CustomAudienceProfilesStyled } from './CustomAudienceProfiles.styles';

import type { CustomAudienceProfileType } from '../customAudienceProfiles.helpers';

type Props = {
  active: string;
  allowedTypes: CustomAudienceProfileType[];
  onChange: (type: string) => void;
};

type ProfileItem = {
  description: string;
  icon: string;
  id: string;
  title: string;
};

function CustomAudienceProfiles({ active, allowedTypes, onChange }: Props) {
  const { formatMessage } = useIntl();

  const items: ProfileItem[] = [
    {
      description: formatMessage({
        defaultMessage: 'Upload a file containing the Device IDs you want to include in your audience profile.',
        id: 'audienceProfile.steps.deviceIds.itemDescription'
      }),
      icon: 'fa-regular fa-file-import',
      id: 'device_ids',
      title: formatMessage({
        defaultMessage: 'Device IDs File',
        id: 'audienceProfile.steps.deviceIds.itemTitle'
      })
    },
    {
      description: formatMessage({
        defaultMessage: 'Import audience IDs derived from a previous campaign to refine your targeting.',
        id: 'audienceProfile.steps.previousCampaignData.itemDescription'
      }),
      icon: 'fa-regular fa-laptop-mobile',
      id: 'previous_campaign_data',
      title: formatMessage({
        defaultMessage: 'Previous Campaign Data',
        id: 'audienceProfile.steps.previousCampaignData.itemTitle'
      })
    },
    {
      description: formatMessage({
        defaultMessage: 'Leverage event data from your campaigns to build a focused audience profile.',
        id: 'audienceProfile.steps.campaignEvents.itemDescription'
      }),
      icon: 'fa-regular fa-arrow-pointer',
      id: 'campaign_events',
      title: formatMessage({
        defaultMessage: 'Campaign Events',
        id: 'audienceProfile.steps.campaignEvents.itemTitle'
      })
    },
    {
      description: formatMessage({
        defaultMessage: 'Target devices based on their visits to specific locations.',
        id: 'audienceProfile.steps.venueVisits.itemDescription'
      }),
      icon: 'fa-kit-duotone fa-poi-inventory',
      id: 'venue_visits',
      title: formatMessage({
        defaultMessage: 'Venue Visits',
        id: 'audienceProfile.steps.venueVisits.itemTitle'
      })
    }
  ];
  const visibleItems = items.filter(item => allowedTypes.includes(item.id as CustomAudienceProfileType));

  return (
    <CustomAudienceProfilesStyled className="customAudienceProfiles">
      <p className="customAudienceProfiles__description">
        {formatMessage({
          defaultMessage: 'Add custom audience profiles tailored to your campaign needs',
          id: 'audienceProfile.steps.profileSettings.title'
        })}
      </p>
      <div className="customAudienceProfiles__cards" role="radiogroup">
        {visibleItems.map(item => {
          const isSelected = item.id === active;
          return (
            <button
              aria-checked={isSelected}
              className={cn('customAudienceProfiles__card', { 'customAudienceProfiles__card--selected': isSelected })}
              id={`customAudienceProfiles-${item.id}`}
              key={item.id}
              onClick={() => onChange(item.id)}
              role="radio"
              type="button"
            >
              <span className="customAudienceProfiles__cardImage">
                <i className={cn('customAudienceProfiles__cardIcon', item.icon)} />
              </span>
              <span className="customAudienceProfiles__cardTitle">{item.title}</span>
              <span className="customAudienceProfiles__cardDescription">{item.description}</span>
            </button>
          );
        })}
      </div>
    </CustomAudienceProfilesStyled>
  );
}

export default CustomAudienceProfiles;
